import React, { useState, useEffect, useContext, useCallback } from 'react';
import axios from 'axios';
// core components
import GridItem from "app/components/Grid/GridItem.js";
import GridContainer from "app/components/Grid/GridContainer.js";
import Card from "app/components/Card/Card.js";
import CardHeader from "app/components/Card/CardHeader.js";
import CardBody from "app/components/Card/CardBody.js";
import "app/layout/style.scss";
import { DataGrid } from '@mui/x-data-grid';
import useWindowSize from 'app/hooks/useWindowSize';
import { LoginStatusContext } from 'app/context/LoginStatusContext';
import { DatePicker } from '@mui/lab';
import TextField from '@mui/material/TextField';
import Button from "@material-ui/core/Button";
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined';
import CircularProgress from '@mui/material/CircularProgress';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import InputLabel from '@mui/material/InputLabel';
import FormControl from '@mui/material/FormControl';
import { processDate } from "app/util/dateUtils";

export default function ShipmentSearch() {

    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [_UserSystemKey] = useState(sessionStorage.getItem('_UserSystemKey'));
    const { setIsLoggedIn } = useContext(LoginStatusContext);
    var date = new Date();
    date.setFullYear(date.getFullYear() - 1);
    const [fromDate, setFromDate] = useState(date);
    const [toDate, setToDate] = useState(new Date());
    const [canSearchQuoteFrom, setCanSearchQuoteFrom] = useState(true);
    const [canSearchQuoteTo, setCanSearchQuoteTo] = useState(true);
    const [selectedSearchType, setSelectedSearchType] = useState('DateAdded');
    const [selectedBookingTypeId, setSelectedBookingTypeId] = useState(20);

    useEffect(() => {
        async function validateUserSessionId() {
            try {
                await axios.get(`${process.env.REACT_APP_API_ENDPOINT}/api/login/ValidateUserSessionId`)
                    .then(() => setIsLoggedIn(true));

            } catch (error) {
                setIsLoggedIn(false);
                window.location.replace(`${process.env.PUBLIC_URL}/login`);
            }
        }
        validateUserSessionId();
    }, [setIsLoggedIn]);

    const windowSize = useWindowSize();

    const getQuotes = useCallback(async () => {
        setLoading(true);
        try {
            const url = `${process.env.REACT_APP_API_ENDPOINT}/api/shipment/getwebportalsearchdata`;
            const params = {
                fromDate: fromDate,
                toDate: toDate,
                searchType: selectedSearchType,
                bookingTypeId: selectedBookingTypeId
            }
            const response = await axios.get(url, { params: params });
            setItems(response.data);
            setLoading(false);
        } catch (error) {
            console.error(`An error occured whilst fetching table data with the error ${error}`);
            setLoading(false);
        }
    }, [fromDate, selectedBookingTypeId, selectedSearchType, toDate])

    useEffect(() => {
        getQuotes();
    }, [getQuotes]);

    const seaColumns = [
        {
            field: 'shipmentID',
            headerName: 'shipmentID',
            type: 'number',
            hide: true
        },
        {
            field: 'shipmentRef',
            headerName: 'Europa Reference',
            minWidth: 130,
            flex: 1
        },
        {
            field: 'validToDate',
            headerName: 'Valid to Date',
            minWidth: 100,
            flex: 1,
            renderCell: (params) => {
                return processDate(params.row.validToDate);
            }
        },
        {
            field: 'shipper',
            headerName: 'Shipper',
            minWidth: 200,
            flex: 1
        },
        {
            field: 'consignee',
            headerName: 'Consignee',
            minWidth: 200,
            flex: 1
        },
        {
            field: 'originPort',
            headerName: 'Origin Port',
            minWidth: 100,
            flex: 1
        },
        {
            field: 'destinationPort',
            headerName: 'Destination Port',
            minWidth: 120,
            flex: 1
        },
        {
            field: 'termType',
            headerName: 'Terms',
            minWidth: 100,
            flex: 1
        },
        {
            field: 'cargoMode',
            headerName: 'Cargo Mode',
            minWidth: 100,
            flex: 1
        },
        {
            field: 'totalCost',
            headerName: <div>Total Cost &pound; *</div>,
            type: 'number',
            minWidth: 120,
            flex: 1,
            renderCell: (params) => {
                const totalCost = params.row.totalCost ?? 0;
                return totalCost.toFixed(2);
            }
        },
    ];

    const airColumns = [
        {
            field: 'shipmentID',
            headerName: 'shipmentID',
            type: 'number',
            hide: true
        },
        {
            field: 'shipmentRef',
            headerName: 'Europa Reference',
            minWidth: 130,
            flex: 1
        },
        {
            field: 'validToDate',
            headerName: 'Valid to Date',
            minWidth: 100,
            flex: 1,
            renderCell: (params) => {
                return processDate(params.row.validToDate);
            }
        },
        {
            field: 'shipper',
            headerName: 'Shipper',
            minWidth: 200,
            flex: 1
        },
        {
            field: 'consignee',
            headerName: 'Consignee',
            minWidth: 200,
            flex: 1
        },
        {
            field: 'originAirport',
            headerName: 'Origin Airport',
            minWidth: 100,
            flex: 1
        },
        {
            field: 'destinationAirport',
            headerName: 'Destination Airport',
            minWidth: 120,
            flex: 1
        },
        {
            field: 'termType',
            headerName: 'Terms',
            minWidth: 100,
            flex: 1
        },
        {
            field: 'totalCost',
            headerName: <div>Total Cost &pound; *</div>,
            type: 'number',
            minWidth: 120,
            flex: 1
        },
    ];

    const handleCellClick = (param, event) => {
        return window.location.href = '/LeoSeaPortal/quotedetails?shipmentId=' + param.row.shipmentID + ''
    };

    const fromDateOnChange = (date) => {
        setFromDate(date);
        if (date) {
            if (date.toLocaleDateString('en-GB').length !== 10) {
                setCanSearchQuoteFrom(false);
            }
            else if (!isNaN(date.getTime())) {
                setCanSearchQuoteFrom(true);
            }
            else {
                setCanSearchQuoteFrom(false);
            }
        }
        else {
            setCanSearchQuoteFrom(false);
        }
    };

    const toDateOnChange = (date) => {
        setToDate(date);
        if (date) {
            if (date.toLocaleDateString('en-GB').length !== 10) {
                setCanSearchQuoteTo(false);
            }
            else if (!isNaN(date.getTime())) {
                setCanSearchQuoteTo(true);
            }
            else {
                setCanSearchQuoteTo(false);
            }
        }
        else {
            setCanSearchQuoteTo(false);
        }
    };

    const fromDateValidation = (error) => {
        if (error) {
            setCanSearchQuoteFrom(false);
        }
    };

    const toDateValidation = (error) => {
        if (error) {
            setCanSearchQuoteTo(false);
        }
    };

    const searchQuote = () => {
        setLoading(true);
        getQuotes();
    };

    const bookingTypeOnChange = (event) => {
        if (event.target.value) {
            setSelectedBookingTypeId(event.target.value);
        }
    }

    const renderSeaTable = (items) => {
        if (selectedBookingTypeId === 20) {
            if (items.length > 0) {
                return (
                    <div id="QuoteSearch">
                        <GridItem xs={12}>
                            <div style={{ marginBottom: '10px', width: '100%', fontSize: '6px' }}>
                                <DataGrid
                                    getRowId={(row) => row.shipmentID}
                                    rows={items}
                                    autoHeight={true}
                                    pageSize={10}
                                    onCellClick={handleCellClick}
                                    columns={seaColumns}
                                    rowHeight={40}
                                    sx={{
                                        fontSize: 'small',
                                        "& .MuiDataGrid-row:hover": {
                                            backgroundColor: "#CBC3E3",
                                            cursor: 'pointer'
                                        }
                                    }}
                                />
                            </div>
                            <p><em>* Currency exchange rates are calculated on the date of quotation and are subject to change.</em></p>
                        </GridItem>
                    </div>
                );
            } else {
                return (<span>No results found...</span>);
            }
        }
    }

    const renderAirTable = (items) => {
        if (selectedBookingTypeId === 19) {
            if (items.length > 0) {
                return (
                    <div id="QuoteSearch">
                        <GridItem xs={12}>
                            <div style={{ marginBottom: '10px', width: '100%', fontSize: '6px' }}>
                                <DataGrid
                                    getRowId={(row) => row.shipmentID}
                                    rows={items}
                                    autoHeight={true}
                                    pageSize={10}
                                    onCellClick={handleCellClick}
                                    columns={airColumns}
                                    rowHeight={40}
                                    sx={{
                                        fontSize: 'small',
                                        "& .MuiDataGrid-row:hover": {
                                            backgroundColor: "#CBC3E3",
                                            cursor: 'pointer'
                                        }
                                    }}
                                />
                            </div>
                            <p><em>* Currency exchange rates are calculated on the date of quotation and are subject to change.</em></p>
                        </GridItem>
                    </div>
                );
            } else {
                return (<span>No results found...</span>);
            }
        }
    }

    let seaContents = loading && selectedBookingTypeId === 20
        ? <CircularProgress style={{ position: 'relative', left: '50%', color: '#2059A7' }}></CircularProgress>
        : renderSeaTable(items.webPortalSeaQuotes);

    let airContents = loading && selectedBookingTypeId === 19
        ? <CircularProgress style={{ position: 'relative', left: '50%', color: '#2059A7' }}></CircularProgress>
        : renderAirTable(items.webPortalAirQuotes);

    return (
        <div style={{ position: 'relative', zIndex: '0' }}>
            <GridContainer>
                <GridItem xs={12} sm={12} md={12}>
                    <Card>
                        <CardHeader style={{ backgroundColor: '#2059A7', color: '#FFF' }}>
                            <h4>Quotes</h4>
                        </CardHeader>
                        <CardBody>
                            <GridItem xs={12} sm={12}>
                                <FormControl style={{ width: 110 }}>
                                    <InputLabel id="bookingtype">Booking Type</InputLabel>
                                    <Select
                                        labelId="bookingtype"
                                        id="bookingtype"
                                        value={selectedBookingTypeId}
                                        label="Booking Type"
                                        size="small"
                                        sx={{ marginRight: 1, marginBottom: 1 }}
                                        onChange={bookingTypeOnChange}>
                                        <MenuItem value={19}>Air</MenuItem>
                                        <MenuItem value={20}>Sea</MenuItem>
                                    </Select>
                                </FormControl>
                                <DatePicker
                                    id="fromDate"
                                    name="fromDate"
                                    label="From"
                                    value={fromDate}
                                    onError={fromDateValidation}
                                    onChange={fromDateOnChange}
                                    renderInput={(params) => <TextField size="small" required sx={{ marginRight: 1 }} {...params} />}
                                />
                                <DatePicker
                                    id="toDate"
                                    name="toDate"
                                    label="To"
                                    value={toDate}
                                    onError={toDateValidation}
                                    onChange={toDateOnChange}
                                    renderInput={(params) => <TextField size="small" required sx={{ marginBottom: 1, marginRight: 1 }} {...params} />}
                                />
                                <Button
                                    style={{ backgroundColor: '#2059A7', color: '#FFF', marginTop: 1 }}
                                    disabled={!canSearchQuoteFrom || !canSearchQuoteTo}
                                    onClick={searchQuote}>
                                    <SearchOutlinedIcon />
                                </Button>
                            </GridItem>
                            {airContents}
                            {seaContents}
                        </CardBody>
                    </Card>
                </GridItem>
            </GridContainer>
        </div>
    );

}
